import { useState, useEffect } from "react";
import { api } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { FileText, Star, MoreVertical, Trash2, Edit } from "lucide-react";
import EditTemplateModal from "./EditTemplateModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

export default function TemplateList({ onRefresh }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTemplate, setEditTemplate] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get("/templates");
      setTemplates(response.data);
    } catch (error) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm("Delete this template?")) return;
    
    try {
      await api.delete(`/templates/${templateId}`);
      toast.success("Template deleted");
      fetchTemplates();
      onRefresh?.();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const handleSetDefault = async (templateId) => {
    try {
      await api.put(`/templates/${templateId}`, { is_default: true });
      toast.success("Template set as default");
      fetchTemplates();
    } catch (error) {
      toast.error("Failed to update template");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="stat-card">
          <div className="stat-value text-primary">{templates.length}</div>
          <div className="stat-label">Total Templates</div>
        </div>
        <div className="stat-card">
          <div className="stat-value text-chart-3">
            {templates.filter(t => t.is_default).length}
          </div>
          <div className="stat-label">Default Templates</div>
        </div>
        <div className="stat-card">
          <div className="stat-value text-chart-2">
            {templates.filter(t => {
              const created = new Date(t.created_at);
              const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
              return created > weekAgo;
            }).length}
          </div>
          <div className="stat-label">Created This Week</div>
        </div>
      </div>

      {/* Template List */}
      {templates.length === 0 ? (
        <div className="empty-state">
          <FileText className="empty-state-icon" />
          <h3 className="text-lg font-semibold mb-2">No templates found</h3>
          <p className="text-muted-foreground">
            Create reusable email templates to speed up campaign creation
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <div 
              key={template.template_id}
              className="bg-card border border-border rounded-lg p-5 card-hover"
              data-testid={`template-card-${template.template_id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{template.name}</h3>
                  {template.is_default && (
                    <Badge className="badge-scheduled">
                      <Star className="h-3 w-3 mr-1" />
                      Default
                    </Badge>
                  )}
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-3 line-clamp-1">
                <span className="font-medium">Subject:</span> {template.subject}
              </p>

              <div className="p-3 bg-secondary/30 rounded-lg mb-4 max-h-24 overflow-hidden">
                <p className="text-sm text-muted-foreground line-clamp-3">
                  {template.content}
                </p>
              </div>

              <div className="text-xs text-muted-foreground mb-4">
                Created {new Date(template.created_at).toLocaleDateString()}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-border">
                {!template.is_default && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSetDefault(template.template_id)}
                  >
                    <Star className="h-3 w-3 mr-1" />
                    Set Default
                  </Button>
                )}
                {template.is_default && <div />}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setEditTemplate(template)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleDelete(template.template_id)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      )}

      {editTemplate && (
        <EditTemplateModal
          template={editTemplate}
          onClose={() => setEditTemplate(null)}
          onSuccess={() => {
            setEditTemplate(null);
            fetchTemplates();
            onRefresh?.();
          }}
        />
      )}
    </div>
  );
}
